import { useEffect, useLayoutEffect, useRef } from "react";
import "./styles/globals.css";
import { Outlet, useLocation } from "react-router-dom";
import { sanitizeVisibleTextNodes } from "../sanitizeMarketingText.js";

export default function SaasLayout() {
  const location = useLocation();
  const initialRoute = useRef(`${location.pathname}${location.search}${location.hash}`);
  const normalizedPathname = location.pathname.replace(/\/+$/, "") || "/";
  const preserveViteRouteStyles = import.meta.env.PROD;

  useLayoutEffect(() => {
    if (location.hash) return;

    const currentRoute = `${location.pathname}${location.search}${location.hash}`;
    if (currentRoute === initialRoute.current) return;

    const root = document.documentElement;
    const body = document.body;
    const previousRootBehavior = root.style.scrollBehavior;
    const previousBodyBehavior = body.style.scrollBehavior;
    const hardScrollTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    root.style.scrollBehavior = "auto";
    body.style.scrollBehavior = "auto";
    hardScrollTop();
    root.style.scrollBehavior = previousRootBehavior;
    body.style.scrollBehavior = previousBodyBehavior;

    return () => {
      root.style.scrollBehavior = previousRootBehavior;
      body.style.scrollBehavior = previousBodyBehavior;
    };
  }, [location.pathname, location.search, location.hash]);

  useLayoutEffect(() => {
    const version =
      (typeof window !== "undefined" && window.localStorage?.getItem("offlico_build_version")) || "";
    const resolveStylesheetHref = (path) =>
      version ? `${path}?v=${encodeURIComponent(version)}` : path;
    const ensureStylesheet = ({ selector, hrefContains, path, dataAttribute }) => {
      const existingLink =
        document.querySelector(selector) ||
        document.querySelector(`link[rel="stylesheet"][href*="${hrefContains}"]`);
      const link =
        existingLink instanceof HTMLLinkElement ? existingLink : document.createElement("link");

      if (!existingLink) {
        link.rel = "stylesheet";
        link.href = resolveStylesheetHref(path);
        link.setAttribute(dataAttribute, "true");
        document.head.appendChild(link);
      }

      return link;
    };
    const setStylesheetState = (link, enabled) => {
      if (!(link instanceof HTMLLinkElement)) return;
      link.disabled = !enabled;
      link.media = enabled ? "all" : "not all";
      link.dataset.active = enabled ? "true" : "false";
    };
    const existingOverrides = document.querySelector('style[data-saas-home-overrides="true"]');
    const homeStylesheet = ensureStylesheet({
      selector: 'link[data-saas-home="true"]',
      hrefContains: "saas-home.css",
      path: "/saas-home.css",
      dataAttribute: "data-saas-home",
    });
    const blogStylesheet = ensureStylesheet({
      selector: 'link[data-blog-system="true"]',
      hrefContains: "blog-system.css",
      path: "/blog-system.css",
      dataAttribute: "data-blog-system",
    });

    setStylesheetState(homeStylesheet, true);
    setStylesheetState(blogStylesheet, true);

    if (existingOverrides) {
      existingOverrides.remove();
    }

    document.body.classList.add("saas-theme");

    const disabledSheets = new Set();
    const disabledLinks = new Set();
    const disableSheet = (sheet) => {
      if (!sheet || sheet.disabled) return;
      sheet.disabled = true;
      disabledSheets.add(sheet);
    };

    const shouldDisableDevStyle = (node) => {
      if (!(node instanceof HTMLStyleElement)) return false;
      if (!node.hasAttribute("data-vite-dev-id")) return false;
      const id = node.getAttribute("data-vite-dev-id") || "";
      return (
        id.endsWith("/src/tailwind.css") ||
        id.endsWith("/src/index.css") ||
        id.endsWith("/src/styles/figma-booking.css") ||
        id.endsWith("/src/components/booking/redesign/figma.css") ||
        id.endsWith("/src/components/booking/redesign/figma-host.css") ||
        id.endsWith("/src/components/layout/MainLayout.css") ||
        id.endsWith("/src/components/layout/SiteShell.css") ||
        id.endsWith("/src/pages/auth/SignInPage.css")
      );
    };

    const disableDevSheets = () => {
      document.querySelectorAll('style[data-vite-dev-id]').forEach((node) => {
        if (!shouldDisableDevStyle(node)) return;
        if (!node.sheet) return;
        disableSheet(node.sheet);
      });
    };

    if (!preserveViteRouteStyles) {
      disableDevSheets();
    }

    let devObserver;
    if (import.meta.env.DEV && !preserveViteRouteStyles) {
      devObserver = new MutationObserver(() => {
        disableDevSheets();
      });
      devObserver.observe(document.head, {
        childList: true,
        subtree: true
      });
    }

    if (import.meta.env.PROD && !preserveViteRouteStyles) {
      const origin = window.location.origin;
      document.querySelectorAll('link[rel="stylesheet"]').forEach((node) => {
        if (!(node instanceof HTMLLinkElement)) return;
        const href = node.href || "";
        if (!href) return;
        if (href.includes("/saas-home.css")) return;
        if (href.includes("/blog-system.css")) return;
        if (href.includes("fonts.googleapis.com")) return;
        if (href.includes("cdn.jsdelivr.net")) return;
        if (!href.startsWith(origin)) return;
        if (!href.endsWith(".css")) return;
        if (!node.disabled) {
          node.disabled = true;
          disabledLinks.add(node);
        }
      });
    }

    return () => {
      document.body.classList.remove("saas-theme");
      devObserver?.disconnect();
      disabledSheets.forEach((sheet) => {
        sheet.disabled = false;
      });
      disabledLinks.forEach((node) => {
        node.disabled = false;
      });
    };
  }, [preserveViteRouteStyles]);

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace("#", "");
    if (!id) return;
    const scrollToTarget = () => {
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };
    const timer = setTimeout(scrollToTarget, 0);
    return () => clearTimeout(timer);
  }, [location.hash, location.pathname]);

  useEffect(() => {
    const root = document.querySelector(".saas-homepage");
    if (!root) return undefined;

    sanitizeVisibleTextNodes(root);

    const observer = new MutationObserver(() => {
      sanitizeVisibleTextNodes(root);
    });

    observer.observe(root, {
      childList: true,
      characterData: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [normalizedPathname]);

  return (
    <div className="saas-homepage">
      <Outlet />
    </div>
  );
}
